import {
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  type SaveResult,
  announceRefusal,
  reportValidityAboveBar,
  useSaveSection,
} from "@/components/SaveBar";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { type ActionOutcome, LOAD_FAILED } from "@/lib/api";

export interface SaveFormProps {
  /** Names the form for screen readers; also tells two forms of one section apart. */
  readonly label: string;
  /** Shown, not editable: an ended action, or a part this role may only read. */
  readonly readOnly: boolean;
  /** The button stays visible but cannot be pressed, e.g. while obstacles remain. */
  readonly disabled?: boolean;
  readonly submit: (form: FormData) => Promise<ActionOutcome>;
  readonly onSaved: () => void;
  /** A named action keeps its own button; without one the form joins the page's save bar. */
  readonly submitLabel?: string;
  /** Changed against what is saved, for a form whose state lives outside its inputs. */
  readonly dirty?: boolean;
  /**
   * Out of sight but mounted, so the bar still saves and checks it (the click editor, D49). A
   * refusal under it stays in sight: the bar names the form, the reason stands there.
   */
  readonly hidden?: boolean;
  readonly children?: ReactNode;
}

/** The text values of a form as one comparable string; files play no part. */
function formState(form: HTMLFormElement | FormData): string {
  const data = form instanceof FormData ? form : new FormData(form);
  return JSON.stringify(
    [...data.entries()].filter(([, value]) => typeof value === "string"),
  );
}

/**
 * One form, one answer under it. The server's sentence is shown as it comes, line breaks
 * included (a refused go-live lists one obstacle per line); a disabled fieldset keeps the
 * values readable where nothing may change.
 *
 * A form without a named action has no button of its own: it joins the page's save bar
 * (`SaveBar`, around every page) and tells it whether its values differ from the last saved
 * ones. For a named action, and where no bar is, the form keeps its button.
 */
export function SaveForm({
  label,
  readOnly,
  disabled = false,
  submit,
  onSaved,
  submitLabel,
  dirty,
  hidden = false,
  children,
}: SaveFormProps): ReactElement {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<ActionOutcome | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // The values as last saved and as on the screen. A disabled fieldset leaves its values out
  // of a FormData, so the saved state is taken from what was sent, never read back while busy.
  const [baseline, setBaseline] = useState<string | null>(null);
  const [current, setCurrent] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (formRef.current !== null) {
      const state = formState(formRef.current);
      setBaseline(state);
      setCurrent(state);
    }
  }, []);

  const send = async (form: FormData): Promise<boolean> => {
    setBusy(true);
    setNotice(null);
    try {
      const outcome = await submit(form);
      setNotice(outcome.ok ? { ok: true, message: "Gespeichert." } : outcome);
      if (!outcome.ok) {
        announceRefusal(formRef.current);
      }
      if (outcome.ok) {
        const state = formState(form);
        setBaseline(state);
        setCurrent(state);
        onSaved();
      }
      return outcome.ok;
    } catch (error: unknown) {
      console.error("[SaveForm] save failed", label, error);
      setNotice({ ok: false, message: LOAD_FAILED });
      announceRefusal(formRef.current);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveInBar = async (): Promise<SaveResult> => {
    const form = formRef.current;
    if (form === null) {
      return "failed";
    }
    if (!reportValidityAboveBar(form)) {
      return "invalid";
    }
    return (await send(new FormData(form))) ? "ok" : "failed";
  };

  const joins = !readOnly && !disabled && submitLabel === undefined;
  const changed = dirty ?? (baseline !== null && current !== baseline);
  const inBar = useSaveSection(joins ? { label, dirty: changed, save: saveInBar } : null);

  const onSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void send(new FormData(event.currentTarget));
  };

  return (
    <form
      ref={formRef}
      aria-label={label}
      hidden={hidden && notice?.ok !== false}
      className="mt-6 max-w-2xl first:mt-0"
      onSubmit={onSubmit}
      onChange={(event) => {
        setCurrent(formState(event.currentTarget));
      }}
    >
      <fieldset disabled={readOnly || busy || disabled}>
        {children}
        {readOnly || inBar ? null : (
          <Button className="mt-4" type="submit">
            {busy ? "Wird gespeichert …" : (submitLabel ?? "Speichern")}
          </Button>
        )}
      </fieldset>
      {/* In the bar, success is the bar's to say; a refusal stays with its section. */}
      {notice !== null && !(inBar && notice.ok) ? (
        <Notice tone={notice.ok ? "success" : "error"} className="mt-3 whitespace-pre-line">
          {/* Out of its part, a refusal names its form: it stands under another element. */}
          {hidden && !notice.ok ? `${label}: ` : null}
          {notice.message ?? "Gespeichert."}
        </Notice>
      ) : null}
    </form>
  );
}
